import dom from 'embo/utils/dom'
import animate from 'embo/utils/animate'

import PageSpinner from 'embo/ui/PageSpinner'
import {unmountReactComponents} from 'embo/ui/react-components'
import {initHeaderWaves, destroyHeaderWaves} from 'embo/ui/header-wave'


const LAYOUTS = {
  fluid: {
    sidebar: false,
    main: 'fluid',
    className: 'layout-fluid',
  },
  centered: {
    sidebar: false,
    main: 'centered',
    className: 'layout-centered-sidebar',
  },
}

const noop = () => {}
const fadeOut = el => animate(el, {opacity: 0}, {duration: 200})
const fadeIn = el => animate(el, {opacity: 1}, {duration: 300})

// TODO: Use the scrollRestoration API when browsers implement it ...
//if ('scrollRestoration' in history) {
//    // Back off, browser, I got this...
//    history.scrollRestoration = 'manual';
//}


export default class PageManager {
  /**
   * @param {PageLoader} loader
   */
  constructor(loader) {
    this._loader = loader
    this.elements = {
      mainContainer: dom.id('main-container'),
      main: dom.id('main'),
      sidebar: dom.id('sidebar'),
    }
    this.currentLayout = LAYOUTS.homepage
    this.previousLayout = null
    this.currentTheme = 'homepage'
    this.previousTheme = null
    this.context = null
    this._spinner = new PageSpinner()
  }

  setContext(ctx) {
    this.context = ctx
    return this
  }

  setLayout(name) {
    this.previousLayout = this.currentLayout
    this.currentLayout = LAYOUTS[name]
    return this
  }

  setTheme(name) {
    this.previousTheme = this.currentTheme
    this.currentTheme = name
    return this
  }

  saveInitialPage() {
    this._loader.save(location.pathname)
  }

  fetch(url) {
    // do not show spinner if page is in cache
    if (!this._loader.needsFetching(url)) {
      return this._loader.load(url)
    }
    // TODO: show spinner only after a certain amount of time (1sec ?)
    return this.showSpinner()
      .then(() => this._loader.load(url))
      .then(result => this.hideSpinner().then(() => result))
  }

  getSurface(id, container = null) {
    return dom.qs(`[data-surface-id="${id}"]`, container)
  }

  swapSurface(id, nextDom, beforeEnter = noop) {
    const surface = this.getSurface(id)
    const nextSurface = this.getSurface(id, nextDom)
    return fadeOut(surface)
      .then(() => {
        unmountReactComponents(null, surface)
        destroyHeaderWaves(surface)
      })
      .then(() => {
        surface.innerHTML = nextSurface.innerHTML
        initHeaderWaves(surface)
        return beforeEnter(surface)
      })
      .then(() => fadeIn(surface))

  }

  fadeOut() {
    const {currentLayout, previousLayout, elements: {main, sidebar}} = this
    const fadeSidebar = Boolean(sidebar) && previousLayout.sidebar !== currentLayout.sidebar

    return Promise.all([
      fadeOut(main),
      fadeSidebar ? fadeOut(sidebar) : Promise.resolve(),
    ]).then(() => {
      unmountReactComponents(null, main)
      destroyHeaderWaves(main)
      if (fadeSidebar) {
        unmountReactComponents(null, sidebar)
        destroyHeaderWaves(sidebar)
      }
    })
  }

  fadeIn() {
    const {currentLayout, previousLayout, elements: {main, sidebar}} = this
    const fadeSidebar = Boolean(sidebar) && currentLayout.sidebar && !previousLayout.sidebar

    return Promise.all([
      fadeIn(main),
      fadeSidebar ? fadeIn(sidebar) : Promise.resolve(),
    ])
  }

  showSpinner() {
    return this._spinner.show()
  }

  hideSpinner() {
    return this._spinner.hide()
  }

  showError(err, transient = true) {
    if (transient) {
      return this._spinner.showTransientError(1000)
    }
    return this._spinner.showFatalError(err, 10000)
  }

  restoreScrollPosition() {
    if (this.context.action === 'PUSH') {
      // Forward action, scroll to top...
      return animate(document.documentElement, 'scroll', {duration: 300, offset: 0})
    }
    // TODO: Backward action, restore scroll position...
    return Promise.resolve()
  }

  updateDOM(nextDom) {
    const {currentLayout, previousLayout, elements: {main, sidebar}} = this

    main.innerHTML = dom.qs('#main', nextDom).innerHTML
    initHeaderWaves(main)
    if (sidebar && currentLayout.sidebar && !previousLayout.sidebar) {
      sidebar.innerHTML = dom.qs('#sidebar', nextDom).innerHTML
      initHeaderWaves(sidebar)
    }
    this._setLayoutClasses()
  }

  updateBodyClass() {
    const theme = `theme-${this.currentTheme}`
    const layout = this.currentLayout.className
    document.body.className = `${theme} ${layout}`
  }

  _setLayoutClasses() {
    const {mainContainer, main, sidebar} = this.elements

    switch (this.currentLayout.main) {
      case 'fluid':
        mainContainer.className = 'container-fluid'
        main.className = 'col-xs-12'
        if (sidebar) sidebar.className = 'col-hidden'
        break
      case 'centered':
        mainContainer.className = 'container'
        main.className = 'col-lg-8'
        if (sidebar) sidebar.className = 'col-lg-4'
        break
    }
  }

}
